import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {AutoComplete, Flex, Input, Space, Spin} from "antd";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import useApi from "@hooks/useApi";
import {passwordGenerator} from "@tools/passwordGenerator";
import Highlight from "@components/Highlight";
import useLinkGenerator from "@extension/hooks/useLinkGenerator";
import Icon from "@extension/Icons";
import { ISearchListItemConfig } from "@tools/CustomInterfaces";
import { getValueFromJSON } from "@tools/jsonUtils";
import DefaultListItem from "./form/formField/SelectDropdownLabel/DefaultListItem";

const GlobalSearch: React.FC = () => {
    const {t, i18n} = useTranslation();
    const {api} = useApi();
    const [loading, setLoading] = useState(false)
    const [items, setItems] = useState<any[]>();
    const [searchValue, setSearchValue] = useState("");
    const [globalSearchLabels, setGlobalSearchLabels] = useState({});
    const lang = i18n.language;
    const timeout = useRef<any>(null);
    const linkGenerator = useLinkGenerator();
    const navigate = useNavigate()

    useEffect(() => {
        try{
            //@ts-ignore
            import("@resources/globalSearchLabel/globalSearchLabels.json").then(result => setGlobalSearchLabels(result)).catch(e => {})
        }
        catch(e) {}
    }, [])

    const getFieldName = useCallback((settings, defaultLang?) => {
        let localeSuffix = ""

        if(settings.locale)
            localeSuffix = `_${defaultLang || lang}`

        const split = (settings.name || "name")?.split(".")
        split[0] = split[0] + localeSuffix
        return  split.join(".")
    }, [lang])

    const labelBuilder = useCallback((item: any, config: ISearchListItemConfig[]) => {
        return (config || []).map(settings => {
   
                let fieldName = getFieldName(settings);
    
                let value = getValueFromJSON(item, fieldName);
    
                if (!value && settings.locale) {
                    fieldName = getFieldName(settings, "en")
                    value = getValueFromJSON(item, fieldName)
                }
    
                if(settings.lookup)
                    value = t(`lookup:${settings.lookup}.${value}`)
    
                if (settings.brackets)
                    value = value || value === 0 ? `(${value})` : ""
    
                if (!value && value !== 0)
                    value = ""
    
                return `${settings.prefix || ""}${value}${settings.suffix || ""}`
            }). join(" ")
    
    }, [getFieldName])

    const search = useCallback((ev) => {
        if(!api.hasOwnProperty("globalSearch"))
            return
        setSearchValue(ev.target.value)
        if (!ev.target.value)
            return;

        setLoading(true)

        if(timeout.current)
            clearTimeout(timeout.current);

        timeout.current = setTimeout(async () => {
            const searchTerm = ev.target.value;

            let query: any = {searchTerm, lang}
            //@ts-ignore
            const dbData = await api.globalSearch.globalSearch(query);
            setItems(dbData.data.data)
            setLoading(false)
        }, 300)
    }, [api, lang]);

    const select = useCallback((name, data) => {
        const index = data.index;
        const item = data.item;
        const currentLinkFn = linkGenerator[`${index}Link`];
        if(currentLinkFn)
            navigate(currentLinkFn(item))
        setSearchValue("")
        setItems([])
    }, [navigate]);

    const onEnter = useCallback(async(ev) => {
        const value = ev?.target ? ev.target.value : ev
        search({target: {value}})
    }, []);

    const onBlur = useCallback(async(ev) => {
        
    }, []);

    const options = useMemo(() => {
        const getLabel = (item, indexName: string) => {
            let labelConfig = globalSearchLabels[indexName]
            if(!labelConfig) {
                return <Highlight text={item.name || ""} highlight={item.highlight}/>
            }
            else {
                return <Flex vertical={true}>
                       {labelConfig.map((row: ISearchListItemConfig[], ind ) => {
                            return <DefaultListItem key={ind} item={item} config={row} /> 
                       })} 
                </Flex>
            }
        }

        return (items || []).reduce((res, indexResult)=> {
            if(indexResult.count) {
                let currentCategory = {
                    label :<Flex gap={5}>
                        {Icon(indexResult.index as string)}
                        <span>{t(`${indexResult.index}:name`)}</span>
                    </Flex>,
                    options : indexResult.data.map((item: any) => {
                        return {
                            label: getLabel(item, indexResult.index),
                            value: item.id,
                            item: item,
                            index: indexResult.index
                        }
                    })
                }
                res.push(currentCategory)
            }
            return res
        }, [])

    }, [items])

    return  <AutoComplete 
        backfill={true}
        options={options} 
        onSelect={select} 
        notFoundContent={loading ? <Space><Spin size="small"/>{t("common:searching")}</Space> : t("common:notFound")}
        popupMatchSelectWidth={false}
        value={searchValue}
        style={{height: 32, overflow: "hidden"}}
    >
        <Input.Search 
            loading={loading} 
            name={passwordGenerator(8)} 
            autoComplete="new-password" 
            onChange={search} 
            onPressEnter={onEnter} 
            onBlur={onBlur} 
            onSearch={onEnter} 
            allowClear={true}
            placeholder={t("common:search")}
            styles={{root: {height: 32}}}
        />
    </AutoComplete>
};

export default  GlobalSearch;

